import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, ChevronRight, RefreshCw, Loader2, AlertTriangle, Eraser } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { resetWorkflowElements } from "@/lib/api";
import { toast } from "sonner";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

/**
 * Docs Out tracking — Monday "Ready for Doc's" deals whose status is
 * already "Doc's Out". These are deals where docs have been raised + sent
 * via Monday but the deal isn't fully funded yet. We surface them as a
 * lightweight tracking list so the legal team can re-open them in the
 * wizard if they need to amend / chase.
 */
export default function DocsOutTrackingSection() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [error, setError] = useState(null);

  // Per-deal re-sync from Monday — pulls the CURRENT offer amount (offers
  // get renegotiated after docs go out) and updates the workflow record too.
  const syncDeal = async (e, deal) => {
    e.stopPropagation();
    const id = deal.sf_number || deal.monday_item_id;
    try {
      setSyncing(id);
      const { data } = await api.post(`/monday/refresh-application/${id}`);
      const c = data?.changes || {};
      if (c.loan_amount) {
        toast.success(
          `${id}: offer re-synced £${Number(c.loan_amount.old || 0).toLocaleString()} → £${Number(c.loan_amount.new || 0).toLocaleString()}`,
          { duration: 9000 }
        );
      } else if (Object.keys(c).length) {
        toast.success(`${id}: synced from Monday (${Object.keys(c).join(", ")})`);
      } else {
        toast.info(`${id}: already up to date with Monday`);
      }
      if ((data?.stages_reset || []).length) {
        toast.warning(`Re-confirm required on workflow: ${data.stages_reset.join(", ")}`, { duration: 10000 });
      }
      (data?.warnings || []).forEach((w) => toast.warning(w, { duration: 10000 }));
      await load();
    } catch (err) {
      toast.error("Sync failed: " + errorDetailMessage(err, err.message));
    } finally {
      setSyncing(null);
    }
  };

  // Reset the deal's DocuSign state so re-issued docs can be created & auto-found
  // (used when the envelope was deleted/voided in DocuSign because docs were wrong)
  const resetDocs = async (e, deal) => {
    e.stopPropagation();
    const id = deal.sf_number || deal.monday_item_id;
    if (!window.confirm(
      `Reset DocuSign docs for ${deal.company_name || id}?\n\nThis clears the envelope link, signing statuses and fraud check on the portal so new documents can be generated and re-found. Use this after deleting/voiding the incorrect envelope in DocuSign.`
    )) return;
    try {
      setResetting(id);
      await resetWorkflowElements(id, ["docusign"]);
      toast.success(`DocuSign docs reset for ${deal.company_name || id} — you can now re-generate and re-send.`);
    } catch (err) {
      toast.error("Reset failed: " + errorDetailMessage(err, err.message));
    } finally {
      setResetting(null);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get("/contracts/monday/ready-for-docs", {
        params: { status_filter: "docs_out", limit: 100 },
      });
      setDeals(data?.deals || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const refresh = async () => {
    try {
      setRefreshing(true);
      await api.post("/contracts/monday/refresh-ready-for-docs");
      toast.success("Synced from Monday");
      await load();
    } catch (err) {
      toast.error("Refresh failed: " + errorDetailMessage(err, err.message));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return null; // Don't take up real estate while loading on first paint
  }

  return (
    <Card className="mb-6" data-testid="docs-out-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2 text-blue-900">
            <FileText className="w-5 h-5 text-blue-700" />
            Docs Out — Sent via Monday
            <Badge variant="secondary" data-testid="docs-out-count">{deals.length}</Badge>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={refresh}
            disabled={refreshing}
            className="h-7 px-2 text-xs"
            data-testid="docs-out-refresh"
          >
            {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Refresh
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Deals on Monday currently in "Doc's Out" — docs have already been raised and sent. Re-open one to amend or re-issue.
        </p>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Could not load: {error}</span>
          </div>
        )}
        {!error && deals.length === 0 && (
          <p className="text-sm text-slate-500">No deals currently in "Doc's Out" status.</p>
        )}
        {deals.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {deals.map((deal) => (
              <button
                key={deal.monday_item_id}
                type="button"
                onClick={() => {
                  const id = deal.sf_number || deal.monday_item_id;
                  navigate(`/legal/onboarding-centre?deal=${encodeURIComponent(id)}`);
                }}
                className="text-left p-3 rounded border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-blue-300 transition-colors group"
                data-testid={`docs-out-row-${deal.sf_number}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-semibold text-slate-700">
                        {deal.sf_number || "—"}
                      </span>
                      {deal.offer_pdf_url && (
                        <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-300 text-blue-700">
                          PDF ✓
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-900 mt-0.5 truncate">
                      {deal.company_name || deal.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      £{(deal.loan_amount || 0).toLocaleString()} · FR {deal.factor_rate} · {deal.loan_term_weeks}w
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 mt-0.5" />
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => syncDeal(e, deal)}
                      className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:underline px-1 py-0.5"
                      title="Offer changed on Monday? Re-pull the current offer amount + deal terms for this deal"
                      data-testid={`sync-deal-${deal.sf_number}`}
                    >
                      {syncing === (deal.sf_number || deal.monday_item_id)
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <RefreshCw className="w-3 h-3" />}
                      Sync offer
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => resetDocs(e, deal)}
                      className="inline-flex items-center gap-1 text-[10px] text-red-500 hover:text-red-700 hover:underline px-1 py-0.5"
                      title="Envelope deleted in DocuSign? Reset the portal's DocuSign state so docs can be re-issued"
                      data-testid={`reset-docs-${deal.sf_number}`}
                    >
                      {resetting === (deal.sf_number || deal.monday_item_id)
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Eraser className="w-3 h-3" />}
                      Reset docs
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
